// Cliente HTTP comun para la API.
//
// Antes cada llamada hacia `res.ok ? await res.json() : []` con un `catch` que
// devolvia `[]`: un 401 por token vencido, un 500 del servidor y "no hay datos"
// terminaban siendo indistinguibles. El usuario veia la tabla vacia sin ningun
// aviso — de ahi que al caducar la sesion simplemente dejaran de aparecer las
// interferencias.
//
// Aca las respuestas se clasifican y el error llega a quien llama.

const AUTH_COOKIE = "asa_hd_auth";

let sesionVencidaNotificada = false;

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
  }
  return null;
};

/**
 * Error de API con el status, para que quien llama pueda distinguir casos.
 */
export class ApiError extends Error {
  constructor(message, { status = null, sesionVencida = false } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.sesionVencida = sesionVencida;
  }
}

// Marca en sessionStorage que ya se aviso. El flag en memoria solo no alcanza:
// window.location.href recarga la app, el modulo se re-evalua con el flag en
// false, y una request que falla despues de la recarga vuelve a avisar — de ahi
// que el mensaje apareciera dos veces.
const YA_AVISADO_KEY = "asa_sesion_vencida_avisada";

const yaSeAviso = () => {
  if (sesionVencidaNotificada) return true;
  try {
    return window.sessionStorage.getItem(YA_AVISADO_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Cierra la sesion y manda al login. Idempotente entre requests simultaneas y
 * a traves de la recarga que provoca la propia redireccion.
 */
export const handleSessionExpired = () => {
  if (yaSeAviso()) return;
  sesionVencidaNotificada = true;
  try {
    window.sessionStorage.setItem(YA_AVISADO_KEY, "1");
  } catch {
    // sessionStorage puede no estar disponible; el flag en memoria alcanza
    // para las requests simultaneas de esta misma carga.
  }

  document.cookie = `${AUTH_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  // La redireccion va primero: alert() bloquea el hilo y deja la app a medias
  // hasta que el usuario acepta. El aviso se muestra ya en el login.
  window.location.replace("/?sesion=vencida");
};

/**
 * Limpia la marca de aviso. La llama el login al montarse, una vez mostrado el
 * mensaje, para que la proxima caducidad se vuelva a avisar.
 */
export const limpiarAvisoSesionVencida = () => {
  sesionVencidaNotificada = false;
  try {
    window.sessionStorage.removeItem(YA_AVISADO_KEY);
  } catch {
    /* no-op */
  }
};

/**
 * true solo si la respuesta indica que el token no sirve mas.
 *
 * 401 siempre lo es. Con 403 hay que mirar el cuerpo: la API lo usa tanto para
 * "Token expirado" / "Token invalido" (campo `message`) como para errores de
 * validacion de negocio (campo `error`), que no tienen nada que ver con la
 * sesion y no deben desloguear al usuario.
 */
export const esSesionVencida = (status, data) => {
  if (status === 401) return true;
  if (status !== 403) return false;
  const msg = String(data?.message ?? "");
  return /token/i.test(msg);
};

const mensajePorStatus = (status, mensajeServidor) => {
  if (mensajeServidor) return mensajeServidor;
  if (status === 400) return "Los datos enviados no son válidos.";
  if (status === 404) return "No se encontró lo que buscabas.";
  if (status >= 500) return "El servidor tuvo un problema. Probá de nuevo en unos minutos.";
  return "No se pudo completar la operación.";
};

/**
 * POST a la API con el token de sesion. Devuelve el JSON parseado.
 * Lanza ApiError si algo falla; ante 401/403 ademas cierra la sesion.
 */
export const apiPost = async (ruta, body = {}, { rawBody = false } = {}) => {
  const url = process.env.REACT_APP_API_ROOT + ruta;
  const headers = { Authorization: `Bearer ${getCookie(AUTH_COOKIE)}` };
  if (!rawBody) headers["Content-Type"] = "application/json";

  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      headers,
      body: rawBody ? body : JSON.stringify(body),
    });
  } catch (err) {
    // Fallo de red: el servidor no respondio.
    throw new ApiError("No se pudo conectar con el servidor. Revisá tu conexión.", { status: null });
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    // Respuesta sin JSON valido (por ej. un HTML de error de un proxy).
    if (res.status === 401) {
      handleSessionExpired();
      throw new ApiError("Tu sesión caducó.", { status: 401, sesionVencida: true });
    }
    if (!res.ok) throw new ApiError(mensajePorStatus(res.status), { status: res.status });
    return null;
  }

  // La API usa 403 para DOS cosas distintas y hay que mirar el cuerpo para
  // distinguirlas: los fallos de token responden { message: "Token expirado" |
  // "Token invalido" }, mientras que las validaciones de negocio ("Tipo de
  // instalacion no valido", "Estado no valido", ...) responden { error: ... }.
  // Tratar todo 403 como sesion vencida echaba al usuario recien logueado.
  if (esSesionVencida(res.status, data)) {
    handleSessionExpired();
    throw new ApiError("Tu sesión caducó.", { status: res.status, sesionVencida: true });
  }

  if (!res.ok) {
    throw new ApiError(mensajePorStatus(res.status, data?.error || data?.message), { status: res.status });
  }

  return data;
};
